// ============================================================================
// TEAM MEMBER LICENSES TAB
// Reuses the same person_licenses model as onboarding so team members can
// manage licenses and fleet-driver designation directly inside WorkItemDetailModal.
// Date: April 2026
// ============================================================================

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/team-member-detail.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/team-member-detail#licenses)
 */

'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Award,
  Calendar,
  Car,
  CheckCircle,
  Loader2,
  Plus,
  ScanLine,
  Shield,
  Trash2,
  Upload,
  UserCheck,
  X,
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { invalidateEffectivePermissionsCache, notifyEffectivePermissionsChanged } from '@/lib/iam/useEffectivePermissions';
import * as notify from '@/lib/notifications';
import UserDocsLink from '@/components/shared/UserDocsLink';
import ReviewImageLightbox, { type ReviewImageAsset } from '@/app/client-zone/modules/total-operations/components/shared/ReviewImageLightbox';
import LicenseDiskScanner, { type BarcodeScanResult } from '@/app/client-zone/modules/total-operations/components/smart-task/components/LicenseDiskScanner';
import SADLLicenseCard from '@/app/client-zone/modules/total-operations/components/smart-task/components/SADLLicenseCard';
import type { SADLData } from '@/lib/sadl/sadlDecoder';

// Flags the tab just persisted, passed to onProfileRefresh so the parent modal
// can merge them into the displayed work item immediately (sidebar/Overview
// update without waiting for the profile refetch).
export interface TeamMemberProfileFlagsPatch {
  is_driver?: boolean;
  is_non_driver?: boolean;
}

// 'none' is a legacy state only — members predating the designation still
// resolve to it, but the dropdown forces a real choice (driver/non-driver)
// instead of offering "no designation" as an option.
type DriverMode = 'none' | 'driver' | 'non-driver';
type SelectableDriverMode = Exclude<DriverMode, 'none'>;

interface TeamMemberLicensesTabProps {
  userId: number;
  employeeId?: number | null;
  isActive: boolean;
  isDark?: boolean;
  initialIsDriver?: boolean;
  initialIsNonDriver?: boolean;
  initialDriverLicenseNumber?: string | null;
  initialDriverLicenseExpiry?: string | null;
  onProfileRefresh?: (patch?: TeamMemberProfileFlagsPatch) => Promise<void> | void;
}

interface TeamMemberLicense {
  id: number;
  license_type: string;
  license_number: string | null;
  license_class?: string | null;
  issue_date?: string | null;
  expiry_date?: string | null;
  issuing_authority?: string | null;
  document_url?: string | null;
  document_filename?: string | null;
  notes?: string | null;
  status?: string | null;
  urgency_level?: string | null;
  days_until_expiry?: number | null;
  type_label?: string | null;
  type_icon?: string | null;
}

interface NewLicenseState {
  type: string;
  license_number: string;
  license_class: string;
  issue_date: string;
  expiry_date: string;
  issuing_authority: string;
  document_url: string;
  document_filename: string;
  notes: string;
}

const LICENSE_TYPES = [
  { value: 'drivers_license', label: "Driver's License", icon: '🚗' },
  { value: 'forklift', label: 'Forklift License', icon: '🚜' },
  { value: 'working_at_heights', label: 'Working at Heights', icon: '🏗️' },
  { value: 'first_aid', label: 'First Aid Certificate', icon: '🩺' },
  { value: 'fire_fighting', label: 'Fire Fighting Certificate', icon: '🔥' },
  { value: 'hazmat', label: 'Hazmat Handling', icon: '☢️' },
  { value: 'crane_operator', label: 'Crane Operator', icon: '🏗️' },
  { value: 'scaffolding', label: 'Scaffolding Certificate', icon: '🪜' },
  { value: 'electrical', label: 'Electrical Certificate', icon: '⚡' },
  { value: 'welding', label: 'Welding Certificate', icon: '🔧' },
  { value: 'confined_spaces', label: 'Confined Spaces', icon: '🕳️' },
  { value: 'heavy_vehicle', label: 'Heavy Vehicle (Code 10/14)', icon: '🚛' },
  { value: 'dangerous_goods', label: 'Dangerous Goods', icon: '⚠️' },
  { value: 'security', label: 'Security License (PSIRA)', icon: '🛡️' },
  { value: 'other', label: 'Other Certification', icon: '📜' },
] as const;

const INITIAL_NEW_LICENSE: NewLicenseState = {
  type: 'drivers_license',
  license_number: '',
  license_class: '',
  issue_date: '',
  expiry_date: '',
  issuing_authority: '',
  document_url: '',
  document_filename: '',
  notes: '',
};

const STATIC_FILE_SERVER_UPLOAD_URL = 'https://staticfileserver.totalaccess.co.za/upload';
const STATIC_FILE_SERVER_API_KEY = 'a11abd7b-0d47-4fe2-a66f-f7c405cf5f80';
const TOTAL_OPERATIONS_MODULE_KEY = 'total-operations';

function cn(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(' ');
}

function isDrivingLicenseType(type?: string | null) {
  return type === 'drivers_license' || type === 'heavy_vehicle';
}

function formatDate(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-ZA');
}

function getExpiryStatus(license: TeamMemberLicense) {
  if (!license.expiry_date) {
    return {
      label: 'No Expiry',
      tone: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      urgent: false,
    };
  }

  const expiry = new Date(license.expiry_date);
  if (Number.isNaN(expiry.getTime())) {
    return {
      label: 'Date Invalid',
      tone: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      urgent: false,
    };
  }

  const diffDays = Math.ceil((expiry.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) {
    return {
      label: 'Expired',
      tone: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
      urgent: true,
    };
  }

  if (diffDays <= 30) {
    return {
      label: `Expires in ${diffDays}d`,
      tone: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
      urgent: true,
    };
  }

  return {
    label: 'Valid',
    tone: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    urgent: false,
  };
}

function TextInput({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:ring-2 focus:ring-orange-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
      />
    </div>
  );
}

function TextAreaInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        rows={2}
        className="w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:ring-2 focus:ring-orange-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
      />
    </div>
  );
}

export default function TeamMemberLicensesTab({
  userId,
  employeeId,
  isActive,
  isDark = false,
  initialIsDriver = false,
  initialIsNonDriver = false,
  initialDriverLicenseNumber,
  initialDriverLicenseExpiry,
  onProfileRefresh,
}: TeamMemberLicensesTabProps) {
  const [licenses, setLicenses] = useState<TeamMemberLicense[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showAddLicense, setShowAddLicense] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDriver, setIsDriver] = useState(initialIsDriver);
  const [isNonDriver, setIsNonDriver] = useState(initialIsNonDriver);
  const [newLicense, setNewLicense] = useState<NewLicenseState>(INITIAL_NEW_LICENSE);
  const [showLicenseScanner, setShowLicenseScanner] = useState(false);
  const [scannedSadl, setScannedSadl] = useState<SADLData | null>(null);
  const [imagePreview, setImagePreview] = useState<{
    images: ReviewImageAsset[];
    initialIndex: number;
    title?: string;
  } | null>(null);

  const openLicensePreview = useCallback((license: TeamMemberLicense) => {
    if (!license.document_url) return;

    setImagePreview({
      images: [{
        url: license.document_url,
        alt: license.document_filename || license.type_label || license.license_type || 'License document',
        caption: license.document_filename || license.type_label || license.license_type || null,
      }],
      initialIndex: 0,
      title: license.type_label || license.document_filename || 'License document',
    });
  }, []);

  const loadLicenses = useCallback(async () => {
    if (!isActive) return;

    setLoading(true);
    setError(null);

    try {
      const token = authService.getTokens()?.access_token;
      const params = new URLSearchParams({ include_expired: 'true' });

      if (employeeId) {
        params.set('employee_id', String(employeeId));
      } else {
        params.set('user_id', String(userId));
      }

      const response = await fetch(`/api/v1/licenses?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.error || 'Failed to load licenses');
      }

      const nextLicenses = Array.isArray(payload.licenses) ? payload.licenses : [];
      setLicenses(nextLicenses);
      setIsDriver((current) => current || nextLicenses.some((license: TeamMemberLicense) => isDrivingLicenseType(license.license_type)));
    } catch (loadError: unknown) {
      console.error('[TeamMemberLicensesTab] load error:', loadError);
      setError(loadError instanceof Error ? loadError.message : 'Failed to load licenses');
    } finally {
      setLoading(false);
    }
  }, [employeeId, isActive, userId]);

  useEffect(() => {
    void loadLicenses();
  }, [loadLicenses]);

  useEffect(() => {
    setIsDriver(initialIsDriver);
  }, [initialIsDriver]);

  useEffect(() => {
    setIsNonDriver(initialIsNonDriver);
  }, [initialIsNonDriver]);

  const drivingLicenses = useMemo(() => {
    return licenses.filter((license) => isDrivingLicenseType(license.license_type));
  }, [licenses]);

  const primaryDrivingLicense = useMemo(() => {
    return drivingLicenses.find((license) => license.status !== 'expired') || drivingLicenses[0] || null;
  }, [drivingLicenses]);

  // A "valid" driver's license has a recorded expiry date that hasn't
  // passed (driving licenses always expire — no date means it can't be
  // verified as valid). Also honours the license stored directly on the
  // member's module permissions when no license records exist yet.
  const hasValidDrivingLicense = useMemo(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const valid = (expiry?: string | null) => {
      if (!expiry) return false;
      const date = new Date(expiry);
      return !Number.isNaN(date.getTime()) && date >= todayStart;
    };

    return (
      drivingLicenses.some((license) => license.status !== 'expired' && valid(license.expiry_date))
      || Boolean(initialDriverLicenseNumber && valid(initialDriverLicenseExpiry))
    );
  }, [drivingLicenses, initialDriverLicenseExpiry, initialDriverLicenseNumber]);

  const summary = useMemo(() => {
    const expired = licenses.filter((license) => getExpiryStatus(license).label === 'Expired').length;
    const expiringSoon = licenses.filter((license) => {
      const status = getExpiryStatus(license);
      return status.urgent && status.label !== 'Expired';
    }).length;

    return {
      total: licenses.length,
      expired,
      expiringSoon,
      driving: drivingLicenses.length,
    };
  }, [drivingLicenses.length, licenses]);

  const syncDriverPermissions = useCallback(async (
    nextIsDriver: boolean,
    nextDriverLicenseNumber?: string | null,
    nextDriverLicenseExpiry?: string | null,
    nextIsNonDriver?: boolean,
  ) => {
    const token = authService.getTokens()?.access_token;
    const response = await fetch('/api/v1/module-access/users', {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        module_key: TOTAL_OPERATIONS_MODULE_KEY,
        is_driver: nextIsDriver,
        driver_license_number: nextDriverLicenseNumber ?? null,
        driver_license_expiry: nextDriverLicenseExpiry ?? null,
        ...(nextIsNonDriver !== undefined ? { smart_task_non_driver: nextIsNonDriver } : {}),
      }),
    });

    const payload = await response.json();
    if (!response.ok || !payload.success) {
      throw new Error(payload.error || 'Failed to update driver permissions');
    }
  }, [userId]);

  const syncNonDriverPermission = useCallback(async (nextIsNonDriver: boolean, nextIsDriver?: boolean) => {
    const token = authService.getTokens()?.access_token;
    const response = await fetch('/api/v1/module-access/users', {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        module_key: TOTAL_OPERATIONS_MODULE_KEY,
        smart_task_non_driver: nextIsNonDriver,
        ...(nextIsDriver !== undefined ? { is_driver: nextIsDriver } : {}),
      }),
    });

    const payload = await response.json();
    if (!response.ok || !payload.success) {
      throw new Error(payload.error || 'Failed to update non-driver mode');
    }
  }, [userId]);

  const refreshProfile = useCallback(async () => {
    await loadLicenses();
    await onProfileRefresh?.();
  }, [loadLicenses, onProfileRefresh]);

  const handleDocumentUpload = useCallback(async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    input.onchange = async (event: Event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;

      setUploadingDocument(true);
      setError(null);
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await fetch(STATIC_FILE_SERVER_UPLOAD_URL, {
          method: 'POST',
          headers: {
            'x-api-key': STATIC_FILE_SERVER_API_KEY,
          },
          body: formDataUpload,
        });

        const payload = await response.json();
        if (!response.ok || !payload.directUrl) {
          throw new Error(payload.error || 'Failed to upload document');
        }

        setNewLicense((current) => ({
          ...current,
          document_url: payload.directUrl,
          document_filename: file.name,
        }));
      } catch (uploadError: unknown) {
        console.error('[TeamMemberLicensesTab] upload error:', uploadError);
        setError(uploadError instanceof Error ? uploadError.message : 'Failed to upload document');
      } finally {
        setUploadingDocument(false);
      }
    };

    input.click();
  }, []);

  const handleReplaceDocument = useCallback(async (license: TeamMemberLicense) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    input.onchange = async (event: Event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;

      setUploadingDocument(true);
      setError(null);
      try {
        const uploadData = new FormData();
        uploadData.append('file', file);

        const uploadResponse = await fetch(STATIC_FILE_SERVER_UPLOAD_URL, {
          method: 'POST',
          headers: {
            'x-api-key': STATIC_FILE_SERVER_API_KEY,
          },
          body: uploadData,
        });

        const uploadPayload = await uploadResponse.json();
        if (!uploadResponse.ok || !uploadPayload.directUrl) {
          throw new Error(uploadPayload.error || 'Failed to upload document');
        }

        const token = authService.getTokens()?.access_token;
        const updateResponse = await fetch('/api/v1/licenses', {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: license.id,
            document_url: uploadPayload.directUrl,
            document_filename: file.name,
          }),
        });

        const updatePayload = await updateResponse.json();
        if (!updateResponse.ok || !updatePayload.success) {
          throw new Error(updatePayload.error || 'Failed to update document');
        }

        setMessage('Document updated');
        await refreshProfile();
      } catch (replaceError: unknown) {
        console.error('[TeamMemberLicensesTab] replace document error:', replaceError);
        setError(replaceError instanceof Error ? replaceError.message : 'Failed to replace document');
      } finally {
        setUploadingDocument(false);
      }
    };

    input.click();
  }, [refreshProfile]);

  // Fleet driver (is_driver) and non-driver (smart_task_non_driver) are
  // mutually exclusive designations — surface them as one dropdown.
  const driverMode: DriverMode = isNonDriver ? 'non-driver' : isDriver ? 'driver' : 'none';

  const handleDriverModeChange = useCallback(async (nextMode: SelectableDriverMode) => {
    if (nextMode === driverMode) {
      return;
    }

    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      if (nextMode === 'driver') {
        // Fleet drivers should hold a valid (unexpired) driver's license —
        // require an explicit acknowledgement before designating without one.
        if (!hasValidDrivingLicense) {
          const acknowledged = await notify.confirm({
            title: "No valid driver's license",
            message: drivingLicenses.length === 0
              ? "This team member has no driver's license on record. You can still designate them as a fleet driver, but add a valid license before assigning them vehicles."
              : "This team member's driver's license is expired or has no valid expiry date. You can still designate them as a fleet driver, but renew the license before assigning them vehicles.",
            confirmText: 'Designate anyway',
            cancelText: 'Cancel',
            variant: 'warning',
            requireConfirmation: {
              type: 'checkbox',
              label: "I acknowledge there is no valid driver's license on record",
            },
          });
          if (!acknowledged) {
            return;
          }
        }

        await syncDriverPermissions(
          true,
          primaryDrivingLicense?.license_number || initialDriverLicenseNumber || null,
          primaryDrivingLicense?.expiry_date || initialDriverLicenseExpiry || null,
          // A designated fleet driver cannot simultaneously be a non-driver
          // passenger — clear the flag in the same write.
          false,
        );
        setIsDriver(true);
        setIsNonDriver(false);
        setMessage('Team member marked as a fleet driver');

        if (!hasValidDrivingLicense) {
          setShowAddLicense(true);
          setNewLicense((current) => ({
            ...current,
            type: 'drivers_license',
          }));
        }
      } else {
        // Non-driver clears the incompatible fleet-driver designation in the
        // same write.
        await syncNonDriverPermission(true, false);
        setIsNonDriver(true);
        setIsDriver(false);
        setMessage('Team member marked as a non-driver — vehicle features are hidden in Smart Task');
      }

      invalidateEffectivePermissionsCache();
      notifyEffectivePermissionsChanged();
      await onProfileRefresh?.({
        is_driver: nextMode === 'driver',
        is_non_driver: nextMode === 'non-driver',
      });
    } catch (modeError: unknown) {
      console.error('[TeamMemberLicensesTab] driver mode error:', modeError);
      setError(modeError instanceof Error ? modeError.message : 'Failed to update driving designation');
    } finally {
      setSaving(false);
    }
  }, [driverMode, drivingLicenses.length, hasValidDrivingLicense, initialDriverLicenseExpiry, initialDriverLicenseNumber, onProfileRefresh, primaryDrivingLicense?.expiry_date, primaryDrivingLicense?.license_number, syncDriverPermissions, syncNonDriverPermission]);

  // SADL PDF417 scan → autofill the license form. The scanner picks the
  // native ML Kit plugin inside the terminal app and the useCamera web
  // scanner in the browser — no separate QR handoff needed.
  const handleSadlScan = useCallback((result: BarcodeScanResult) => {
    setShowLicenseScanner(false);
    if (result.type !== 'drivers_license') return;

    const { sadl } = result;
    setScannedSadl(sadl);
    setNewLicense((current) => ({
      ...current,
      type: 'drivers_license',
      license_number: sadl.licenseNumber || current.license_number,
      license_class:
        sadl.vehicleLicenses.map((vehicle) => vehicle.code).filter(Boolean).join(' ')
        || current.license_class,
      issue_date: sadl.licenseValidityStart || current.issue_date,
      expiry_date: sadl.licenseValidityExpiry || current.expiry_date,
    }));
  }, []);

  const resetAddLicenseForm = useCallback(() => {
    setShowAddLicense(false);
    setNewLicense(INITIAL_NEW_LICENSE);
    setScannedSadl(null);
  }, []);

  const handleAddLicense = useCallback(async () => {
    if (!newLicense.type || !newLicense.license_number) {
      setError('License type and number are required');
      return;
    }

    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch('/api/v1/licenses', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          employee_id: employeeId || null,
          license_type: newLicense.type,
          license_number: newLicense.license_number,
          license_class: newLicense.license_class || null,
          issue_date: newLicense.issue_date || null,
          expiry_date: newLicense.expiry_date || null,
          issuing_authority: newLicense.issuing_authority || null,
          document_url: newLicense.document_url || null,
          document_filename: newLicense.document_filename || null,
          notes: newLicense.notes || null,
        }),
      });

      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.error || 'Failed to add license');
      }

      if (isDrivingLicenseType(newLicense.type)) {
        await syncDriverPermissions(true, newLicense.license_number, newLicense.expiry_date || null);
        setIsDriver(true);
      }

      setMessage('License added successfully');
      setNewLicense(INITIAL_NEW_LICENSE);
      setScannedSadl(null);
      setShowAddLicense(false);
      await refreshProfile();
    } catch (saveError: unknown) {
      console.error('[TeamMemberLicensesTab] add error:', saveError);
      setError(saveError instanceof Error ? saveError.message : 'Failed to add license');
    } finally {
      setSaving(false);
    }
  }, [employeeId, newLicense, refreshProfile, syncDriverPermissions, userId]);

  const handleDeleteLicense = useCallback(async (license: TeamMemberLicense) => {
    setDeletingId(license.id);
    setError(null);
    setMessage(null);

    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(`/api/v1/licenses?id=${license.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.error || 'Failed to delete license');
      }

      const remainingLicenses = licenses.filter((current) => current.id !== license.id);
      const remainingDrivingLicenses = remainingLicenses.filter((current) => isDrivingLicenseType(current.license_type));
      const nextPrimaryDrivingLicense = remainingDrivingLicenses.find((current) => current.status !== 'expired') || remainingDrivingLicenses[0] || null;

      if (isDrivingLicenseType(license.license_type)) {
        await syncDriverPermissions(
          remainingDrivingLicenses.length > 0 ? isDriver : false,
          nextPrimaryDrivingLicense?.license_number || null,
          nextPrimaryDrivingLicense?.expiry_date || null,
        );
        if (remainingDrivingLicenses.length === 0) {
          setIsDriver(false);
        }
      }

      setMessage('License removed');
      await refreshProfile();
    } catch (deleteError: unknown) {
      console.error('[TeamMemberLicensesTab] delete error:', deleteError);
      setError(deleteError instanceof Error ? deleteError.message : 'Failed to delete license');
    } finally {
      setDeletingId(null);
    }
  }, [isDriver, licenses, refreshProfile, syncDriverPermissions]);

  return (
    <div className="space-y-5">
      <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'} shadow-sm`}>
        <div className="flex items-start gap-3">
          <Award className="mt-0.5 h-5 w-5 text-orange-500" />
          <div className="min-w-0 flex-1">
            <h3 className={cn('text-sm font-semibold', isDark ? 'text-gray-100' : 'text-gray-900')}>
              Licenses & certifications
              <UserDocsLink docsPath="/total-operations/workflows/service-operations/team-member-detail#licenses" label="User guide: Licenses" className="ml-1 align-middle" />
            </h3>
            <p className={cn('mt-1 text-xs', isDark ? 'text-gray-400' : 'text-gray-500')}>
              This uses the same license records as onboarding, so fleet, compliance, and person management stay in sync.
            </p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            { label: 'Total records', value: summary.total, tone: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
            { label: 'Driving licenses', value: summary.driving, tone: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
            { label: 'Expiring soon', value: summary.expiringSoon, tone: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
            { label: 'Expired', value: summary.expired, tone: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
          ].map((card) => (
            <div key={card.label} className={`rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
              <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {card.label}
              </p>
              <div className="mt-2 flex items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${card.tone}`}>
                  {card.value}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'} shadow-sm`}>
        <div className="flex items-center gap-2">
          <Car className="h-4 w-4 text-orange-500" />
          <p className={cn('text-sm font-semibold', isDark ? 'text-gray-100' : 'text-gray-900')}>
            Driving designation
          </p>
        </div>
        <p className={cn('mt-1 text-xs', isDark ? 'text-gray-400' : 'text-gray-500')}>
          Fleet driver lists use this flag. Non-drivers ride along as assistants — Smart Task hides vehicle
          selection and inspections for them. Every team member is either a fleet driver or a non-driver.
        </p>
        <div className="mt-3">
          <select
            value={driverMode === 'none' ? '' : driverMode}
            onChange={(event) => void handleDriverModeChange(event.target.value as SelectableDriverMode)}
            disabled={saving}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:ring-2 focus:ring-orange-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          >
            {driverMode === 'none' && (
              <option value="" disabled>
                Select a driving designation…
              </option>
            )}
            <option value="driver">Fleet driver</option>
            <option value="non-driver">Non-driver (assistant / passenger)</option>
          </select>
        </div>
        {driverMode === 'none' && (
          <div className={cn('mt-3 flex items-center gap-2 rounded-lg px-3 py-2 text-xs', isDark ? 'bg-amber-900/30 text-amber-200' : 'bg-amber-50 text-amber-700')}>
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-amber-500" />
            <span>No driving designation set — choose fleet driver or non-driver.</span>
          </div>
        )}
        {driverMode === 'driver' && (primaryDrivingLicense || initialDriverLicenseNumber || initialDriverLicenseExpiry) && (
          <div className={cn('mt-3 rounded-lg px-3 py-2 text-xs', isDark ? 'bg-gray-900/70 text-gray-300' : 'bg-gray-50 text-gray-600')}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold">Primary driver license:</span>
              <span>{primaryDrivingLicense?.license_number || initialDriverLicenseNumber || 'Not set'}</span>
              {(primaryDrivingLicense?.expiry_date || initialDriverLicenseExpiry) && (
                <span>• Expires {formatDate(primaryDrivingLicense?.expiry_date || initialDriverLicenseExpiry)}</span>
              )}
            </div>
          </div>
        )}
        {driverMode === 'non-driver' && (
          <div className={cn('mt-3 flex items-center gap-2 rounded-lg px-3 py-2 text-xs', isDark ? 'bg-gray-900/70 text-gray-300' : 'bg-gray-50 text-gray-600')}>
            <UserCheck className="h-3.5 w-3.5 flex-shrink-0 text-slate-500" />
            <span>Vehicle features are hidden for this member — removing this restores the driver tools.</span>
          </div>
        )}
      </div>

      {(message || error) && (
        <div className={cn(
          'rounded-xl border px-4 py-3 text-sm',
          error
            ? (isDark ? 'border-red-900/60 bg-red-900/20 text-red-200' : 'border-red-200 bg-red-50 text-red-700')
            : (isDark ? 'border-emerald-900/60 bg-emerald-900/20 text-emerald-200' : 'border-emerald-200 bg-emerald-50 text-emerald-700')
        )}>
          {error || message}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="mr-2 h-6 w-6 animate-spin text-orange-500" />
          <span className={cn('text-sm', isDark ? 'text-gray-400' : 'text-gray-500')}>Loading licenses…</span>
        </div>
      ) : (
        <>
          {!showAddLicense ? (
            <button
              type="button"
              onClick={() => setShowAddLicense(true)}
              className={cn(
                'w-full rounded-xl border-2 border-dashed px-4 py-4 text-sm font-medium transition-colors flex items-center justify-center gap-2',
                isDark
                  ? 'border-gray-700 text-gray-300 hover:border-orange-500 hover:text-orange-300'
                  : 'border-gray-300 text-gray-600 hover:border-orange-400 hover:text-orange-600'
              )}
            >
              <Plus className="h-4 w-4" />
              Add license or certificate
            </button>
          ) : (
            <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'} shadow-sm space-y-4`}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h4 className={cn('text-sm font-semibold', isDark ? 'text-gray-100' : 'text-gray-900')}>
                    Add new license
                  </h4>
                  <p className={cn('mt-1 text-xs', isDark ? 'text-gray-400' : 'text-gray-500')}>
                    This mirrors the onboarding license workflow, including document uploads.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={resetAddLicenseForm}
                  className={cn('rounded-lg p-2', isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100')}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  License type
                </label>
                <select
                  value={newLicense.type}
                  onChange={(event) => setNewLicense((current) => ({ ...current, type: event.target.value }))}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:ring-2 focus:ring-orange-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                >
                  {LICENSE_TYPES.map((licenseType) => (
                    <option key={licenseType.value} value={licenseType.value}>
                      {licenseType.icon} {licenseType.label}
                    </option>
                  ))}
                </select>
              </div>

              {isDrivingLicenseType(newLicense.type) && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    License card barcode (SADL)
                  </label>
                  {scannedSadl ? (
                    <div className="space-y-2">
                      <SADLLicenseCard sadl={scannedSadl} />
                      {scannedSadl.licenseValidityExpiry
                        && new Date(`${scannedSadl.licenseValidityExpiry}T23:59:59`).getTime() < Date.now() && (
                        <div className={cn('flex items-center gap-2 rounded-lg px-3 py-2 text-xs', isDark ? 'bg-amber-900/30 text-amber-200' : 'bg-amber-50 text-amber-700')}>
                          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-amber-500" />
                          <span>The scanned license is expired — the record can still be saved for audit purposes.</span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowLicenseScanner(true)}
                        className={cn(
                          'flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium',
                          isDark
                            ? 'border-gray-600 text-gray-300 hover:border-orange-500 hover:text-orange-300'
                            : 'border-gray-300 text-gray-600 hover:border-orange-400 hover:text-orange-600'
                        )}
                      >
                        <ScanLine className="h-3.5 w-3.5" />
                        Rescan barcode
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowLicenseScanner(true)}
                      className={cn(
                        'flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-4 text-sm font-medium transition-colors',
                        isDark
                          ? 'border-gray-700 text-gray-300 hover:border-orange-500 hover:text-orange-300'
                          : 'border-gray-300 text-gray-600 hover:border-orange-400 hover:text-orange-600'
                      )}
                    >
                      <ScanLine className="h-4 w-4" />
                      Scan driver's license card
                    </button>
                  )}
                  <p className={cn('mt-1.5 text-[11px]', isDark ? 'text-gray-500' : 'text-gray-400')}>
                    Scans the PDF417 barcode on the card — uses the native scanner in the terminal app or this device&apos;s camera in the browser. Number, class and dates are filled in automatically.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <TextInput
                  label="License / certificate number"
                  value={newLicense.license_number}
                  onChange={(value) => setNewLicense((current) => ({ ...current, license_number: value }))}
                  placeholder="e.g. ABC123456"
                />
                <TextInput
                  label="License class"
                  value={newLicense.license_class}
                  onChange={(value) => setNewLicense((current) => ({ ...current, license_class: value }))}
                  placeholder="e.g. Code 10 / B"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <TextInput
                  label="Issue date"
                  type="date"
                  value={newLicense.issue_date}
                  onChange={(value) => setNewLicense((current) => ({ ...current, issue_date: value }))}
                />
                <TextInput
                  label="Expiry date"
                  type="date"
                  value={newLicense.expiry_date}
                  onChange={(value) => setNewLicense((current) => ({ ...current, expiry_date: value }))}
                />
              </div>

              <TextInput
                label="Issuing authority"
                value={newLicense.issuing_authority}
                onChange={(value) => setNewLicense((current) => ({ ...current, issuing_authority: value }))}
                placeholder="e.g. Department of Transport"
              />

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Supporting document
                </label>
                {newLicense.document_url ? (
                  <div className={`rounded-xl border p-3 ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex flex-col gap-3 md:flex-row md:items-start">
                      <img
                        src={newLicense.document_url}
                        alt="License document"
                        className="h-32 w-full rounded-lg border border-gray-200 object-cover dark:border-gray-700 md:w-40"
                      />
                      <div className="min-w-0 flex-1">
                        <p className={cn('text-sm font-medium', isDark ? 'text-gray-200' : 'text-gray-800')}>
                          {newLicense.document_filename || 'Uploaded document'}
                        </p>
                        <div className="mt-3 flex gap-2">
                          <button
                            type="button"
                            onClick={() => setNewLicense((current) => ({ ...current, document_url: '', document_filename: '' }))}
                            className="rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/20"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => void handleDocumentUpload()}
                    disabled={uploadingDocument}
                    className={cn(
                      'flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-sm font-medium transition-colors',
                      isDark
                        ? 'border-gray-700 text-gray-300 hover:border-orange-500 hover:text-orange-300'
                        : 'border-gray-300 text-gray-600 hover:border-orange-400 hover:text-orange-600'
                    )}
                  >
                    {uploadingDocument ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Uploading…
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4" />
                        Upload document image
                      </>
                    )}
                  </button>
                )}
              </div>

              <TextAreaInput
                label="Notes"
                value={newLicense.notes}
                onChange={(value) => setNewLicense((current) => ({ ...current, notes: value }))}
                placeholder="Any extra compliance or renewal notes"
              />

              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={resetAddLicenseForm}
                  className={cn(
                    'flex-1 rounded-lg border px-4 py-2 text-sm font-medium',
                    isDark ? 'border-gray-700 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  )}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleAddLicense()}
                  disabled={saving || !newLicense.license_number}
                  className="flex-1 rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Add license'}
                </button>
              </div>
            </div>
          )}

          {licenses.length === 0 && !showAddLicense ? (
            <div className={cn('rounded-xl border border-dashed px-4 py-12 text-center', isDark ? 'border-gray-700 bg-gray-900/40 text-gray-400' : 'border-gray-200 bg-gray-50 text-gray-500')}>
              <Shield className="mx-auto mb-3 h-12 w-12 opacity-50" />
              <p className="text-sm font-medium">No licenses or certifications yet</p>
              <p className="mt-1 text-xs">Add the first one to track expiry, documents, and fleet-driver readiness.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {licenses.map((license) => {
                const status = getExpiryStatus(license);
                return (
                  <div
                    key={license.id}
                    className={cn(
                      'rounded-xl border p-4 shadow-sm',
                      isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white',
                      status.urgent && (isDark ? 'ring-1 ring-amber-800/60' : 'ring-1 ring-amber-200')
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-start gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-lg dark:bg-orange-900/30">
                          {license.type_icon || LICENSE_TYPES.find((item) => item.value === license.license_type)?.icon || '📜'}
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className={cn('text-sm font-semibold', isDark ? 'text-gray-100' : 'text-gray-900')}>
                              {license.type_label || LICENSE_TYPES.find((item) => item.value === license.license_type)?.label || license.license_type}
                            </h4>
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${status.tone}`}>
                              {status.label}
                            </span>
                            {isDrivingLicenseType(license.license_type) && (
                              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                Driving
                              </span>
                            )}
                          </div>
                          {license.license_number && (
                            <p className={cn('mt-1 text-sm', isDark ? 'text-gray-300' : 'text-gray-700')}>
                              {license.license_number}
                              {license.license_class ? ` • ${license.license_class}` : ''}
                            </p>
                          )}
                          <div className={cn('mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs', isDark ? 'text-gray-400' : 'text-gray-500')}>
                            {license.expiry_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3.5 w-3.5" />
                                Expires {formatDate(license.expiry_date)}
                              </span>
                            )}
                            {license.issuing_authority && (
                              <span className="flex items-center gap-1">
                                <Shield className="h-3.5 w-3.5" />
                                {license.issuing_authority}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => void handleDeleteLicense(license)}
                        disabled={deletingId === license.id}
                        className={cn(
                          'rounded-lg p-2 transition-colors',
                          isDark ? 'text-red-300 hover:bg-red-900/20' : 'text-red-600 hover:bg-red-50'
                        )}
                        title="Remove license"
                      >
                        {deletingId === license.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </div>

                    {(license.document_url || license.notes) && (
                      <div className="mt-4 grid gap-3 md:grid-cols-[160px_1fr]">
                        {license.document_url ? (
                          <button
                            type="button"
                            onClick={() => openLicensePreview(license)}
                            className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
                          >
                            <img
                              src={license.document_url}
                              alt={license.document_filename || 'License document'}
                              className="h-32 w-full object-cover"
                            />
                          </button>
                        ) : (
                          <div className={cn('flex h-32 items-center justify-center rounded-lg border border-dashed text-xs', isDark ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400')}>
                            No document uploaded
                          </div>
                        )}

                        <div className="flex flex-col gap-3">
                          {license.notes && (
                            <div className={cn('rounded-lg px-3 py-2 text-sm', isDark ? 'bg-gray-900/70 text-gray-300' : 'bg-gray-50 text-gray-600')}>
                              {license.notes}
                            </div>
                          )}

                          <div className="flex flex-wrap gap-2">
                            {license.document_url && (
                              <a
                                href={license.document_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                              >
                                View document
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => void handleReplaceDocument(license)}
                              disabled={uploadingDocument}
                              className="rounded-lg border border-orange-300 px-3 py-1.5 text-xs font-medium text-orange-700 hover:bg-orange-50 dark:border-orange-800 dark:text-orange-300 dark:hover:bg-orange-900/20"
                            >
                              Replace document
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {summary.expired > 0 && (
        <div className={cn('rounded-xl border px-4 py-3 text-sm', isDark ? 'border-red-900/60 bg-red-900/20 text-red-200' : 'border-red-200 bg-red-50 text-red-700')}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <p>
              {summary.expired} license{summary.expired === 1 ? '' : 's'} {summary.expired === 1 ? 'has' : 'have'} expired. Renew them here so fleet allocation and compliance records stay current.
            </p>
          </div>
        </div>
      )}

      {isDriver && (primaryDrivingLicense || initialDriverLicenseNumber) && (
        <div className={cn('rounded-xl border px-4 py-3 text-sm', isDark ? 'border-emerald-900/60 bg-emerald-900/20 text-emerald-200' : 'border-emerald-200 bg-emerald-50 text-emerald-700')}>
          <div className="flex items-start gap-2">
            <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <p>
              This team member is currently eligible for Fleet driver assignment{(primaryDrivingLicense?.license_number || initialDriverLicenseNumber) ? ` with ${primaryDrivingLicense?.license_number || initialDriverLicenseNumber}` : ''}.
            </p>
          </div>
        </div>
      )}

      <ReviewImageLightbox
        isOpen={Boolean(imagePreview)}
        images={imagePreview?.images || []}
        initialIndex={imagePreview?.initialIndex || 0}
        title={imagePreview?.title}
        onClose={() => setImagePreview(null)}
      />

      {showLicenseScanner && (
        <LicenseDiskScanner
          onScan={handleSadlScan}
          onClose={() => setShowLicenseScanner(false)}
          scanMode="drivers_license"
          title="Scan Driver's License"
          isDark={isDark}
        />
      )}
    </div>
  );
}
